import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Group, List, Stack, Text } from '@mantine/core';
import type { PtwTypeForm } from '@isg/shared-types';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  useFormKayit,
  useFormKayitAc,
  useFormKayitGuncelle,
  useIzinFormlari,
  useSahaGunu,
} from '../api/queries';
import { QueryStatus } from './parts';
import {
  FormAlanlari,
  KayitGorunumu,
  digerEksigiMetni,
  digerEksikleri,
  digerMetinleriniOku,
  gonderilecekDigerMetinleri,
  semayiCoz,
} from './form-parts';
import type { CevapHaritasi, DigerMetinleri } from './form-parts';
import { ANLAM } from '../theme';

/**
 * PTW TÜR FORMLARI — İZİN İÇİNDE FORM (F4·S8, S8-1..S8-4; F3·S6'nın
 * "Formlar ekranında doldur" bağlantısının yerini aldı).
 *
 * İKİ YÜZEY, İKİNCİ DOLDURMA MODÜLÜ YOK:
 *  · **TALEP EKRANI (S8-1):** izin türü seçilince o türün şablonu (en son
 *    YAYINLI sürüm) talep ekranının İÇİNDE düzenlenebilir açılır; "Taslağı
 *    kaydet" form kayıtlarını izne bağlar. Ayrı sayfaya yönlendirme YOK.
 *  · **İZİN DETAYI:** taslak izinde (revizyona dönüş dahil) form burada
 *    düzenlenir; gönderilmiş formda kayıt KENDİ (gönderim) sürümüyle salt
 *    okunur gösterilir.
 *
 * KURALLAR EKRANDA YENİDEN YAZILMAZ:
 *  · Zorunluluk denetimi SUNUCUDA, talep GÖNDERİMİNDE koşar (S8-3) — taslak
 *    kaydetme kilitsizdir; ekran yalnız `zorunlu` yıldızını gösterir.
 *  · Düğme görünürlüğü sunucudan: `yetkiler.turFormuDoldurabilir` (S8-2 —
 *    kapı iznin kapısıdır, talebi açabilen formunu doldurur).
 *  · Kayıt yazma form motorunun kendi uçlarıdır (POST/PATCH form-submissions
 *    + `ptwPermitId`) — ikinci kayıt yolu yazılmaz.
 *
 * ŞABLONU OLMAYAN TÜR (ör. "Diğer") listede GÖRÜNMEZ: sistem olmayan içeriği
 * uydurmaz. İçeriği gelmemiş türde "içerik tanımlı değil" notu görünür.
 */

/**
 * ESKİ NÜSHALARIN BRİFİNG ALANLARI (✏️ 28.09.2026 — Kapsam §14 K-B1). Brifing
 * tür şablonundan ÇIKTI (iznin kendi kaydı oldu); kütüphane artık bu alanları
 * dağıtmaz. Dağıtım yalnız eklediği için MEVCUT nüshalarda duruyor olabilirler
 * (§13/12) — "içerik var mı" sayımı onları türe özel içerik SAYMAZ.
 */
const ESKI_BRIFING_ALAN_KODLARI = new Set(['brifing_yapildi', 'brifing_katilimcilari']);

function icerikVarMi(form: PtwTypeForm): boolean {
  const sema = semayiCoz(form.sema);
  if (!sema) return false;
  return sema.alanlar.some((alan) => !alan.gizli && !ESKI_BRIFING_ALAN_KODLARI.has(alan.kod));
}

function FormBasligi({ form }: { form: PtwTypeForm }): React.JSX.Element {
  return (
    <Group gap="xs" justify="space-between">
      <Text fw={600}>{form.izinTuruAd}</Text>
      <Group gap="xs">
        <Badge variant="light" size="sm">
          sürüm {form.surumNo}
        </Badge>
        {form.submissionId && (
          <Badge
            size="sm"
            color={form.submissionDurum === 'submitted' ? ANLAM.olumlu : ANLAM.bilgi}
            data-testid="izin-tur-formu-durum"
          >
            {form.submissionDurum === 'submitted' ? 'Gönderildi' : 'Taslak'}
          </Badge>
        )}
      </Group>
    </Group>
  );
}

function IcerikYokNotu({ form }: { form: PtwTypeForm }): React.JSX.Element | null {
  if (icerikVarMi(form)) return null;
  return (
    <Text size="sm" c="dimmed" data-testid="izin-tur-formu-icerik-yok">
      Bu tür için tehlike / önlem / güvenlik ekipmanı listesi henüz tanımlı değil.
    </Text>
  );
}

/**
 * TALEP EKRANI (S8-1) — seçilen türlerin şablonu talep formunun İÇİNDE
 * düzenlenebilir açılır; cevap durumu ÜST bileşende yaşar (taslak kaydetmede
 * izinle birlikte kayda döner). Sorgu da üstte: kayıt açma listeyi oradan
 * okur (ikinci sorgu/ikinci doldurma yüzeyi yok).
 *
 * Lookup beslemeli alan tür şablonlarında yoktur (İç1 içerikleri gömülü
 * seçeneklerle iner) — kalem listesi boş geçilir (eski DoldurmaModali ile
 * aynı sınır).
 */
export function IzinTuruFormGirisleri({
  turSecili,
  formlar,
  cevaplar,
  digerMetinleri,
  onCevaplar,
}: {
  turSecili: boolean;
  formlar: UseQueryResult<PtwTypeForm[]>;
  cevaplar: Record<string, CevapHaritasi>;
  /** K-D1 — şablon başına "Diğer" metinleri (cevaplarla aynı anahtar). */
  digerMetinleri: Record<string, DigerMetinleri>;
  onCevaplar: (formTemplateId: string, yeni: CevapHaritasi, yeniDiger: DigerMetinleri) => void;
}): React.JSX.Element {
  if (!turSecili) {
    return (
      <Alert color={ANLAM.bilgi} title="Türe özel form alanları" data-testid="talep-yer-tutucu">
        <Text size="sm">
          İzin türü seçilince o türün tehlike, önlem ve güvenlik ekipmanı alanları burada açılır ve
          talep içinde doldurulur.
        </Text>
      </Alert>
    );
  }
  return (
    <Stack gap="xs" data-testid="talep-tur-formlari">
      <Text fw={600} size="sm">
        Türe özel form alanları
      </Text>
      <QueryStatus queries={[formlar]} />
      {formlar.isSuccess && formlar.data.length === 0 && (
        <Text size="sm" c="dimmed" data-testid="talep-tur-formu-yok">
          Seçilen tür(ler) için şablon tanımlı değil.
        </Text>
      )}
      {(formlar.data ?? []).map((form) => {
        const sema = semayiCoz(form.sema);
        return (
          <Card key={form.formTemplateId} withBorder padding="sm" data-testid="izin-tur-formu">
            <Stack gap={6}>
              <FormBasligi form={form} />
              <IcerikYokNotu form={form} />
              {sema && (
                <FormAlanlari
                  sema={sema}
                  cevaplar={cevaplar[form.formTemplateId] ?? {}}
                  digerMetinleri={digerMetinleri[form.formTemplateId] ?? {}}
                  onChange={(yeni, yeniDiger) => onCevaplar(form.formTemplateId, yeni, yeniDiger)}
                  lookupKalemleri={[]}
                />
              )}
            </Stack>
          </Card>
        );
      })}
      <Text size="xs" c="dimmed">
        {/* ✏️ KA-8: cümle yalnız TÜR FORMUNUN zorunlu alanlarını anlatır — taslak
            minimumu (işyeri + lokasyon) ayrıdır ve düğmenin yanında söylenir. */}
        Tür formundaki zorunlu alanlar taslak kaydını engellemez; talep onaya gönderilirken
        denetlenir.
      </Text>
    </Stack>
  );
}

/**
 * İZİN DETAYI — tür başına form kartı: taslak izinde DÜZENLENEBİLİR (S8-1 —
 * revizyona dönen talepte de), aksi hâlde salt okunur. `duzenlenebilir`
 * SUNUCU yetkisi (S8-2) ile iznin taslak hâlinin birleşimidir; ekran ikinci
 * yetki kuralı yazmaz — kaydetme ucu da aynı kapıdan reddeder.
 */
export function IzinTuruFormBolumu({
  permitId,
  workplaceId,
  izinTaslak,
  doldurabilir,
}: {
  permitId: string;
  workplaceId: string;
  izinTaslak: boolean;
  doldurabilir: boolean;
}): React.JSX.Element | null {
  const formlar = useIzinFormlari(permitId);
  if (formlar.isSuccess && formlar.data.length === 0) return null;
  return (
    <Card withBorder data-testid="izin-tur-form-bolumu">
      <Stack gap="xs">
        <Text fw={600}>Türe özel form alanları</Text>
        <QueryStatus queries={[formlar]} />
        {(formlar.data ?? []).map((form) => (
          <DetayFormKarti
            key={form.formTemplateId}
            form={form}
            permitId={permitId}
            workplaceId={workplaceId}
            duzenlenebilir={izinTaslak && doldurabilir && form.submissionDurum !== 'submitted'}
          />
        ))}
      </Stack>
    </Card>
  );
}

function DetayFormKarti({
  form,
  permitId,
  workplaceId,
  duzenlenebilir,
}: {
  form: PtwTypeForm;
  permitId: string;
  workplaceId: string;
  duzenlenebilir: boolean;
}): React.JSX.Element {
  const kayit = useFormKayit(form.submissionId);
  const kayitAc = useFormKayitAc();
  // Madde 302: doldurma tarihi SAHA GÜNÜ (sunucudan); kabuk ön yükler.
  const sahaGunu = useSahaGunu();
  const guncelle = useFormKayitGuncelle();
  // Cevaplar ve "Diğer" metinleri TEK yerel durumdur: birlikte değişir,
  // birlikte kaydedilir (K-D1).
  const [duzenlenen, setDuzenlenen] = useState<{
    cevaplar: CevapHaritasi;
    diger: DigerMetinleri;
  } | null>(null);
  const sema = semayiCoz(form.sema);
  // Mevcut kaydın cevapları düzenlemenin BAŞLANGIÇ değeridir; yerel durum
  // yalnız kullanıcı dokununca doğar (sunucu tazelenince üzerine yazılmaz).
  const kayitCevaplari = useMemo<CevapHaritasi>(
    () => Object.fromEntries((kayit.data?.cevaplar ?? []).map((c) => [c.alanKodu, c.deger])),
    [kayit.data],
  );
  const kayitDigerleri = useMemo<DigerMetinleri>(
    () => digerMetinleriniOku(kayit.data?.cevaplar ?? []),
    [kayit.data],
  );
  const cevaplar = duzenlenen?.cevaplar ?? kayitCevaplari;
  const digerMetinleri = duzenlenen?.diger ?? kayitDigerleri;
  const beklemede = kayitAc.isPending || guncelle.isPending;
  const hata = kayitAc.error ?? guncelle.error;
  // K-D1 — "Diğer" seçilip açıklaması yazılmamışsa kaydet PASİFTİR ve sebebi
  // yanında yazar (sunucu aynı kaydı 422 ile reddeder — §2.40).
  const digerEksigi = digerEksigiMetni(sema ? digerEksikleri(sema, cevaplar, digerMetinleri) : []);

  const kaydet = (): void => {
    const secenek = { onSuccess: () => setDuzenlenen(null) };
    const digerGovdesi = sema ? gonderilecekDigerMetinleri(sema, cevaplar, digerMetinleri) : {};
    if (form.submissionId) {
      // ⚠️ İzin türü formunda MADDE NOTU yoktur (kanıt ayarları ürün
      // alanlarında kapalı); gövde yine de TAM gönderilir — `notlar` da
      // `cevaplar` gibi "gövde listenin tamamıdır" sözleşmesindedir.
      guncelle.mutate(
        { id: form.submissionId, girdi: { cevaplar, notlar: {}, digerMetinleri: digerGovdesi } },
        secenek,
      );
    } else {
      // Kayıt YOKSA taslak açılır ve izne bağlanır — ikinci kayıt yolu yok,
      // form motorunun kendi ucudur (`ptwPermitId` gövdede).
      if (!sahaGunu.data) return;
      kayitAc.mutate(
        {
          formTemplateId: form.formTemplateId,
          workplaceId,
          doldurmaTarihi: sahaGunu.data,
          cevaplar,
          notlar: {},
          digerMetinleri: digerGovdesi,
          taslak: true,
          ptwPermitId: permitId,
        },
        secenek,
      );
    }
  };

  return (
    <Card withBorder padding="sm" data-testid="izin-tur-formu">
      <Stack gap={6}>
        <FormBasligi form={form} />
        <IcerikYokNotu form={form} />
        {form.submissionId && <QueryStatus queries={[kayit]} />}
        {sema &&
          (duzenlenebilir ? (
            <>
              <FormAlanlari
                sema={sema}
                cevaplar={cevaplar}
                digerMetinleri={digerMetinleri}
                onChange={(yeni, yeniDiger) => setDuzenlenen({ cevaplar: yeni, diger: yeniDiger })}
                lookupKalemleri={[]}
              />
              {hata && (
                <Alert color={ANLAM.engel} role="alert" data-testid="izin-tur-formu-hata">
                  {hata.message}
                </Alert>
              )}
              <Group justify="flex-end" align="center">
                {digerEksigi !== null && (
                  <Text size="xs" c="dimmed" data-testid="izin-tur-formu-kaydet-sebep">
                    {digerEksigi}
                  </Text>
                )}
                <Button
                  size="xs"
                  data-testid="izin-tur-formu-kaydet"
                  loading={beklemede}
                  disabled={duzenlenen === null || digerEksigi !== null}
                  onClick={kaydet}
                >
                  Formu kaydet (taslak)
                </Button>
              </Group>
            </>
          ) : form.submissionId ? (
            kayit.isSuccess && (
              <KayitGorunumu sema={sema} cevaplar={kayit.data.cevaplar} lookupKalemleri={[]} />
            )
          ) : (
            <>
              <List size="sm" spacing={2}>
                {sema.alanlar
                  .filter((alan) => !alan.gizli)
                  .map((alan) => (
                    <List.Item key={alan.kod}>{alan.etiket}</List.Item>
                  ))}
              </List>
              <Text size="xs" c="dimmed" data-testid="izin-tur-formu-doldurulmadi">
                Bu tür formu doldurulmadı.
              </Text>
            </>
          ))}
        {/* S8-2: doldurma yetkisi olmayan (talep eden olmayan) kullanıcıya
            düğme HİÇ çıkmaz; uç da aynı yüklemle 403 verir. */}
        {!duzenlenebilir && !form.submissionId && (
          <Text size="xs" c="dimmed" data-testid="izin-tur-formu-yetki-yok">
            Formu iznin talep edeni, talep taslaktayken doldurur.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
